import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Trash2, Car } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureRemoveItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import './styles.css';

interface RecentlyViewedVehiclesProps {
  config: any;
}

interface ViewedVehicle {
  id: number;
  timestamp: number;
}

export const RecentlyViewedVehicles = ({ config }: RecentlyViewedVehiclesProps) => {
  const { vehicles } = useAppData();
  const { selectedStore } = useStore();
  const [recentlyViewed, setRecentlyViewed] = useState<any[]>([]);
  const [slugs, setSlugs] = useState<Record<number, string>>({});
  
  // Determine filtering logic based on unifiedAds and unifiedAdsStore
  const shouldFilterByStore = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;
    
    // Don't filter if unifiedAds is false (already handled by useFilteredVehicles)
    if (!unifiedAds) return true;
    
    // Don't filter if unifiedAdsStore is null
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) return false;
    
    // Filter if selected store is different from unifiedAdsStore
    return selectedStoreId !== unifiedAdsStore;
  }, [config?.unifiedAds, config?.unifiedAdsStore, selectedStore?._id]);

  // Load recently viewed vehicles from localStorage
  useEffect(() => {
    const loadRecentlyViewed = async () => {
      try {
        const viewedStr = await secureGetItem('recentlyViewedVehicles');
        if (viewedStr) {
          const viewedIds: ViewedVehicle[] = JSON.parse(viewedStr);

          // Sort by timestamp (most recent first) and get last 6
          const sortedIds = viewedIds
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 6);

          // Get vehicle data for each ID and preserve timestamp
          let viewedVehicles = sortedIds
            .map(viewed => {
              const vehicle = vehicles.find(v => v.id === viewed.id);
              return vehicle ? { ...vehicle, timestamp: viewed.timestamp } : null;
            })
            .filter(vehicle => vehicle !== null);
          
          // Filter by selected store if needed
          if (shouldFilterByStore && selectedStore?._id) {
            viewedVehicles = viewedVehicles.filter(vehicle => vehicle.store === selectedStore._id);
          }

          setRecentlyViewed(viewedVehicles);
        }
      } catch (error) {
        console.error('Erro ao carregar veículos vistos recentemente:', error);
      }
    };

    loadRecentlyViewed();

    // Listen for updates when vehicles are viewed
    const handleStorageChange = () => {
      loadRecentlyViewed();
    };

    window.addEventListener('vehicleViewed', handleStorageChange);
    return () => window.removeEventListener('vehicleViewed', handleStorageChange);
  }, [vehicles, shouldFilterByStore, selectedStore?._id]);

  // Load slugs for recently viewed vehicles
  useEffect(() => {
    const loadSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const vehicle of recentlyViewed) {
        newSlugs[vehicle.id] = await generateVehicleSlug(vehicle);
      }
      setSlugs(newSlugs);
    };
    if (recentlyViewed.length > 0) {
      loadSlugs();
    }
  }, [recentlyViewed]);

  const clearRecentlyViewed = async () => {
    await secureRemoveItem('recentlyViewedVehicles');
    setRecentlyViewed([]);
    window.dispatchEvent(new Event('vehicleViewed'));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} horas atrás`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dias atrás`;
  };

  if (recentlyViewed.length === 0) {
    return null; // Don't render if no recently viewed vehicles
  }

  return (
    <section className="layout-3 wl3-section bg-white border-t border-[var(--wl3-border)]">
      <div className="wl3-container">
        <div className="wl3-section-header !mb-5">
          <div>
            <p className="wl3-eyebrow">Continuar de onde parou</p>
            <h2 className="wl3-h2 mt-1">Vistos recentemente</h2>
          </div>
          {recentlyViewed.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearRecentlyViewed} className="rounded-lg text-muted-foreground shrink-0">
              <Trash2 className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Limpar</span>
            </Button>
          )}
        </div>

        {/* Vehicles Grid */}
        <div className="flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto md:overflow-x-visible snap-x snap-mandatory scrollbar-hide pb-2 md:pb-0">
          {/* Real Vehicles */}
          {recentlyViewed.map((vehicle, index) => {
            const vehicleSlug = slugs[vehicle.id] || '';
            return (
              <Link
                key={`${vehicle.id}-${index}`}
                to={`/estoque/${vehicleSlug}`}
                className="group flex-shrink-0 w-[45%] md:w-auto snap-start"
              >
                <Card className="l3-card overflow-hidden border-0 rounded-2xl shadow-none">
                  <CardContent className="p-0">
                    {/* Vehicle Image */}
                    <div className="relative aspect-[12/7] overflow-hidden">
                      <img
                        src={vehicle.photos[0]}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className="w-full h-full object-cover transition-transform duration-300"
                      />

                      {/* Time Badge */}
                      <Badge
                        variant="secondary"
                        className="absolute top-2 left-2 bg-black/70 text-white text-xs"
                      >
                        {formatTimeAgo(vehicle.timestamp)}
                      </Badge>
                    </div>

                    {/* Vehicle Info */}
                    <div className="p-3">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-sm line-clamp-1 text-foreground truncate">
                            {vehicle.brand} {vehicle.model}
                          </h3>

                          {/* Zero KM Badge */}
                          {vehicle.isZeroKm && (
                            <Badge
                              variant="default"
                              className="bg-green-600 ml-1 text-white text-xs text-center flex items-center whitespace-nowrap"
                            >
                              Zero KM
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>{vehicle.modelYear}</span>
                          {vehicle.mileage >= 0 && (
                            <>
                              <span>•</span>
                              <span>{vehicle.mileage.toLocaleString('pt-BR')} km</span>
                            </>
                          )}
                        </div>

                        <div className="text-sm font-bold text-primary">
                          {formatPrice(vehicle.price)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}

          {/* Placeholder Cards */}
          {recentlyViewed.length < 6 && Array.from({ length: 6 - recentlyViewed.length }).map((_, index) => (
            <Card
              key={`placeholder-${index}`}
              className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex-shrink-0 w-[45%] md:w-auto snap-start"
            >
              <CardContent className="p-0">
                {/* Placeholder Image */}
                <div className="relative aspect-[12/7] overflow-hidden bg-muted/30 flex items-center justify-center">
                  <Eye className="h-8 w-8 text-muted-foreground/30" />
                </div>

                {/* Placeholder Info */}
                <div className="p-3">
                  <div className="space-y-2">
                    <div className="h-4 bg-muted/50 rounded w-3/4"></div>
                    <div className="h-3 bg-muted/30 rounded w-1/2"></div>
                    <div className="h-4 bg-muted/40 rounded w-2/3"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
